/**
 * Remix block control: positioned at the right edge of the block.
 * Uses anchor(anchorName end) or getRightPositionForRootElement (same coordinate system
 * as left controls). The widget span has no position:relative so position:absolute
 * uses the same containing block as the node.
 */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { CSSProperties } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports -- Ignored via go/DSP-18766; jsx required at runtime for @jsxRuntime classic
import { css, jsx } from '@emotion/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { tableControlsSpacing } from '@atlaskit/editor-common/styles';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import {
	getAnchorAttrName,
	getControlBottomCSSValue,
	getControlHeightCSSValue,
	getNodeHeight,
	getRightPositionForRootElement,
	getTopPosition,
	refreshAnchorName,
	rootElementGap,
	STICKY_CONTROLS_TOP_MARGIN_FOR_STICKY_HEADER,
	shouldBeSticky,
	topPositionAdjustment,
	VisibilityContainer,
} from '@atlaskit/editor-plugin-block-controls/block-decoration-utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { shouldShowRemixForSelectionContext } from '../remix-config';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';

const REMIX_BUTTON_HEIGHT = 24;
const REMIX_BUTTON_WIDTH = 24;
const REMIX_BUTTON_DIMENSIONS = { width: REMIX_BUTTON_WIDTH, height: REMIX_BUTTON_HEIGHT };
const REMIX_BUTTON_RIGHT_OFFSET = 55;
// Column layout (layoutSection) has breakout resize handles extending to the right; push Remix button further so it doesn't overlap
const REMIX_BUTTON_RIGHT_OFFSET_LAYOUT_SECTION = 55 + 36;

const INNER_CONTAINER_SELECTORS: Record<string, string> = {
	embedCard: '.rich-media-item',
	table: '.resizer-item',
	mediaSingle: '.resizer-item',
	extension: '.extension-container[data-layout]',
	bodiedExtension: '.extension-container[data-layout]',
	blockCard: '.datasourceView-content-inner-wrap',
};

const getInnerContainerAndEdgeCase = (
	dom: HTMLElement,
	rootNodeType: string,
): { innerContainer: HTMLElement | null; isEdgeCase: boolean } => {
	const selector = INNER_CONTAINER_SELECTORS[rootNodeType];
	const innerContainer = selector ? (dom.querySelector(selector) as HTMLElement | null) : null;
	// Edge case: nodes with nested inner containers (resizer, extension, embedCard, blockCard)
	// require offset-based positioning including dom.offsetLeft because anchor-based positioning
	// doesn't correctly account for the nested layout. Occurs when the inner container exists and
	// the block is not aligned to the right edge of the editor content area.
	const isEdgeCase = selector !== undefined && innerContainer !== null;
	return { innerContainer, isEdgeCase };
};

const containerBaseStyles = css({
	position: 'absolute',
	zIndex: 100,
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
});

const stickyButtonContainerStyles = css({
	top: '8px',
	position: 'sticky',
	display: 'block',
	zIndex: 100,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// Force circle shape — IconButton strips CSS overrides so we target from the parent.
		// Without this, the design system's spacingCompactT26Shape style can override the circle
		// border-radius when platform-dst-shape-theme-default is enabled.
		borderRadius: token('radius.full'),
	},
});

const stickyButtonContainerStylesStickyHeader = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-blocks-remix-button-container]:has(~ [data-prosemirror-node-name="table"] .pm-table-with-controls tr.sticky) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: tableControlsSpacing,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-prosemirror-mark-name="breakout"]:has([data-blocks-remix-button-container]):has(~ [data-prosemirror-node-name="table"] .pm-table-with-controls tr.sticky) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: tableControlsSpacing,
	},
});

const stickyButtonContainerStylesImprovedStickyHeader = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-blocks-remix-button-container]:has(~ [data-prosemirror-node-name="table"] .pm-table-with-controls tr.sticky) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: tableControlsSpacing,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-blocks-remix-button-container]:has(~ [data-prosemirror-mark-name="fragment"] >[data-prosemirror-node-name="table"] tr.pm-table-row-native-sticky.pm-table-row-native-sticky-active) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: `${STICKY_CONTROLS_TOP_MARGIN_FOR_STICKY_HEADER}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-blocks-remix-button-container]:has(~ [data-prosemirror-node-name="table"] tr.pm-table-row-native-sticky.pm-table-row-native-sticky-active) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: `${STICKY_CONTROLS_TOP_MARGIN_FOR_STICKY_HEADER}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-blocks-remix-button-container]:has(~ [data-prosemirror-mark-name="fragment"] >[data-prosemirror-node-name="table"] .pm-table-with-controls tr.sticky) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: tableControlsSpacing,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-prosemirror-mark-name="breakout"]:has([data-blocks-remix-button-container]):has(~ [data-prosemirror-node-name="table"] .pm-table-with-controls tr.sticky) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: tableControlsSpacing,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-prosemirror-mark-name="breakout"]:has([data-blocks-remix-button-container]):has(~ [data-prosemirror-mark-name="fragment"] >[data-prosemirror-node-name="table"] .pm-table-with-controls tr.sticky) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: tableControlsSpacing,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-prosemirror-mark-name="breakout"]:has([data-blocks-remix-button-container]):has(~ [data-prosemirror-node-name="table"] tr.pm-table-row-native-sticky.pm-table-row-native-sticky-active) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: `${STICKY_CONTROLS_TOP_MARGIN_FOR_STICKY_HEADER}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'[data-prosemirror-mark-name="breakout"]:has([data-blocks-remix-button-container]):has(~ [data-prosemirror-mark-name="fragment"] >[data-prosemirror-node-name="table"] tr.pm-table-row-native-sticky.pm-table-row-native-sticky-active) &':
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		top: `${STICKY_CONTROLS_TOP_MARGIN_FOR_STICKY_HEADER}px`,
	},
});

type RemixButtonDecorationProps = {
	anchorName: string;
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	getPos: () => number | undefined;
	isDisabled?: boolean;
	isRovoLLMEnabled?: boolean;
	livePage?: boolean;
	remixDisabledReason?: RemixDisabledReason;
	rootAnchorName?: string;
	rootNodeType: string;
	rootPos: number;
	view: EditorView;
};

export const RemixButtonDecoration = ({
	view,
	api,
	getPos,
	anchorName,
	isDisabled,
	isRovoLLMEnabled,
	livePage,
	remixDisabledReason,
	rootAnchorName,
	rootNodeType,
	rootPos,
}: RemixButtonDecorationProps): jsx.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const {
		macroInteractionUpdates,
		editorViewMode,
		activeNode,
		rightSideControlsEnabled,
		hoverSide,
		contentId,
		isRemixing,
		areAiEntryPointsDisabled,
	} = useSharedPluginStateWithSelector(
		api,
		[
			'featureFlags',
			'editorViewMode',
			'blockControls',
			'contextIdentifier',
			'malleableUi',
			'aiExperience',
		],
		(states) => ({
			macroInteractionUpdates: states.featureFlagsState?.macroInteractionUpdates,
			editorViewMode: states.editorViewModeState?.mode,
			activeNode: states.blockControlsState?.activeNode,
			rightSideControlsEnabled: states.blockControlsState?.rightSideControlsEnabled ?? false,
			hoverSide: states.blockControlsState?.hoverSide,
			contentId: states.contextIdentifierState?.contextIdentifierProvider?.objectId,
			isRemixing:
				states.malleableUiState?.isRemixModalOpen ||
				false ||
				states.malleableUiState?.isThinking ||
				false,
			areAiEntryPointsDisabled: fg('platform_editor_ai_toggle_entry_points')
				? (states.aiExperienceState?.areAiEntryPointsDisabled ?? false)
				: false,
		}),
	);
	const isViewMode = editorViewMode === 'view';

	// pageMode for analytics: 'editor' = full page editor, 'liveEdit' = live page edit, 'liveView' = live page view (livePage set by Confluence).
	const pageMode =
		editorViewMode === 'edit' && !livePage
			? 'editor'
			: editorViewMode === 'edit' && livePage
				? 'liveEdit'
				: editorViewMode === 'view' && livePage
					? 'liveView'
					: undefined;

	// Fire experiment exposure only when the user would see the Remix tool (hover over right side of block)
	const hasFiredExposureRef = useRef(false);
	useEffect(() => {
		if (hasFiredExposureRef.current) {
			return;
		}
		const isActiveBlock =
			activeNode?.rootPos === rootPos &&
			activeNode?.rootAnchorName === (rootAnchorName ?? anchorName);
		const isRightSideVisible = rightSideControlsEnabled && (hoverSide === 'right' || isViewMode);
		if (isActiveBlock && isRightSideVisible) {
			fg('confluence_remix_button_right_side_block_fg');
			hasFiredExposureRef.current = true;
		}
	}, [
		activeNode?.rootPos,
		activeNode?.rootAnchorName,
		rootPos,
		rootAnchorName,
		anchorName,
		rightSideControlsEnabled,
		hoverSide,
		isViewMode,
	]);

	const [positionStyles, setPositionStyles] = useState<React.CSSProperties>({ display: 'none' });

	// Shared offset-based positioning for view mode and edit-mode fallback.
	// Horizontal (left) uses getRightPositionForRootElement; vertical uses getTopPosition.
	const rightOffset =
		rootNodeType === 'layoutSection'
			? REMIX_BUTTON_RIGHT_OFFSET_LAYOUT_SECTION
			: REMIX_BUTTON_RIGHT_OFFSET;

	const getOffsetBasedPosition = useCallback(
		(dom: HTMLElement, safeAnchorName: string): CSSProperties => {
			const { innerContainer, isEdgeCase } = getInnerContainerAndEdgeCase(dom, rootNodeType);
			const isExtension = rootNodeType === 'extension' || rootNodeType === 'bodiedExtension';
			const isMacroInteractionUpdates = macroInteractionUpdates && isExtension;
			const isSticky = shouldBeSticky(rootNodeType);
			const rightEdgeLeft = getRightPositionForRootElement(
				dom,
				rootNodeType,
				REMIX_BUTTON_DIMENSIONS,
				innerContainer ?? undefined,
				isMacroInteractionUpdates,
			);
			const height = getControlHeightCSSValue(
				getNodeHeight(dom, safeAnchorName) || 0,
				isSticky,
				true,
				`${REMIX_BUTTON_DIMENSIONS.height}`,
			);
			return {
				left: isEdgeCase
					? `calc(${dom.offsetLeft}px + (${rightEdgeLeft}) + ${rightOffset}px)`
					: `calc(${rightEdgeLeft} + ${rightOffset}px)`,
				top: getTopPosition(dom, rootNodeType),
				...height,
				bottom: 'unset',
			} as CSSProperties;
		},
		[rootNodeType, macroInteractionUpdates, rightOffset],
	);

	// Edit mode: anchor-based when supported, otherwise offset-based (same as view mode).
	const calculatePosition = useCallback((): CSSProperties => {
		const safeAnchorName = refreshAnchorName({
			getPos,
			view,
			anchorName: rootAnchorName ?? anchorName,
		});
		const dom: HTMLElement | null = view.dom.querySelector(
			`[${getAnchorAttrName()}="${safeAnchorName}"]`,
		);
		if (!dom) {
			return { display: 'none' };
		}

		const isSticky = shouldBeSticky(rootNodeType);

		// View mode always uses offset-based (anchor positioning can differ in view mode).
		if (isViewMode) {
			return getOffsetBasedPosition(dom, safeAnchorName);
		}

		const { isEdgeCase } = getInnerContainerAndEdgeCase(dom, rootNodeType);

		// Check anchor first (no reflow). Only use offset-based when fallback needed.
		const supportsAnchorRight =
			CSS.supports('left', `anchor(${safeAnchorName} end)`) &&
			CSS.supports('top', `anchor(${safeAnchorName} start)`);
		if (supportsAnchorRight && !isEdgeCase) {
			return {
				left: `calc(anchor(${safeAnchorName} end) - ${REMIX_BUTTON_DIMENSIONS.width
					}px - ${rootElementGap(rootNodeType)}px + ${rightOffset}px)`,
				top: `calc(anchor(${safeAnchorName} start) + ${topPositionAdjustment(rootNodeType)}px)`,
				...getControlBottomCSSValue(safeAnchorName, isSticky, true),
			} as CSSProperties;
		}
		return getOffsetBasedPosition(dom, safeAnchorName);
	}, [
		view,
		getPos,
		anchorName,
		rootAnchorName,
		rootNodeType,
		isViewMode,
		getOffsetBasedPosition,
		rightOffset,
	]);

	// Recompute button position on mount and when extension/embedCard layout changes (e.g. expand/collapse).
	// For extension/embedCard we listen to transitionend so position updates after CSS transitions finish.
	useEffect(() => {
		let cleanUpTransitionListener: (() => void) | undefined;
		if (rootNodeType === 'extension' || rootNodeType === 'embedCard') {
			const anchorDom = view.dom.querySelector(
				`[${getAnchorAttrName()}="${rootAnchorName ?? anchorName}"]`,
			);
			if (anchorDom) {
				cleanUpTransitionListener = bind(anchorDom, {
					type: 'transitionend',
					listener: () => setPositionStyles(calculatePosition()),
				});
			}
		}
		const id = requestAnimationFrame(() => setPositionStyles(calculatePosition()));

		return () => {
			cancelAnimationFrame(id);
			cleanUpTransitionListener?.();
		};
	}, [calculatePosition, view.dom, rootAnchorName, rootNodeType, anchorName, isViewMode]);

	const experienceTracker = useAIMateExperienceTracker();

	const handleClick = useCallback(() => {
		if (isDisabled) {
			return;
		}
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'blockNudge',
				product: 'confluence',
			},
		});
		api?.core.actions.execute(
			api?.malleableUi.commands.openRemixModal({
				nodePos: getPos(),
				nodeType: rootNodeType,
				metadata: { interactionSource: 'remixModalFromBlockControls' },
				source: 'blockNudge',
			}),
		);
	}, [api, getPos, isDisabled, experienceTracker, rootNodeType]);

	// Re-affirm activeNode when entering the remix button to prevent disappearing when moving
	// from block toward the button. Skip if already active to avoid flickering from re-dispatch.
	const handleMouseEnter = useCallback(() => {
		const anchor = rootAnchorName ?? anchorName;
		if (activeNode?.rootAnchorName === anchor && activeNode?.rootPos === rootPos) {
			return;
		}
		api?.core?.actions.execute(
			api?.blockControls?.commands.showDragHandleAt(
				rootPos,
				anchor,
				rootNodeType,
				undefined,
				rootPos,
				anchor,
				rootNodeType,
			),
		);
	}, [
		api,
		rootPos,
		anchorName,
		rootAnchorName,
		rootNodeType,
		activeNode?.rootAnchorName,
		activeNode?.rootPos,
	]);

	const isRemixAvailable = isRovoLLMEnabled && expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
	// Use block-controls shared state (activeNode) + reusable filter instead of DOM checks for restricted nodes.
	// When activeNode is null (e.g. on initial render), default to showing the button — the anchor/position
	// logic handles visibility. Only block when we have an explicit restricted node type.
	const activeNodeType = activeNode?.rootNodeType ?? activeNode?.nodeType;
	const showRemixButton = activeNodeType === undefined || shouldShowRemixForSelectionContext({ nodeType: activeNodeType });
	if (!isRemixAvailable || !showRemixButton || areAiEntryPointsDisabled) {
		return null;
	}

	return (
		<VisibilityContainer api={api} controlSide="right">
			<div
				css={containerBaseStyles}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic positioning (left, top, height) calculated at runtime
				style={positionStyles}
				data-testid="block-ctrl-remix-button"
				onMouseEnter={handleMouseEnter}
				onFocus={handleMouseEnter}
			>
				<span
					css={[
						stickyButtonContainerStyles,
						fg('confluence_remix_button_right_side_block_fg')
							? stickyButtonContainerStylesImprovedStickyHeader
							: stickyButtonContainerStylesStickyHeader,
					]}
				>
					<Tooltip
						content={
							isRemixing
								? formatMessage(messages.remixInProgressTooltip)
								: isDisabled
									? getRemixDisabledTooltip(formatMessage, remixDisabledReason)
									: formatMessage(messages.remixBlockTooltip)
						}
						position="top"
					>
						<IconButton
							spacing="compact"
							appearance="subtle"
							shape="circle"
							label={formatMessage(messages.remixBlockLabel)}
							icon={() => <AiGenerativeRemixIcon label="" size="small" />}
							isDisabled={isDisabled || isRemixing}
							testId="visible-block-ctrl-remix-button"
							analyticsContext={{
								actionSubjectId: 'remix',
								source: 'remixModalFromViewPageNudge',
								attributes: {
									...(pageMode !== undefined && { pageMode }),
									nodeType: rootNodeType,
									...(contentId !== undefined && contentId !== '' && { contentId }),
								},
							}}
							onMouseDown={(e) => e.preventDefault()}
							onClick={handleClick}
						/>
					</Tooltip>
				</span>
			</div>
		</VisibilityContainer>
	);
};
